import { useState, useMemo, useEffect } from 'react'
import { Box, Grid, Card, CardContent, Typography, Avatar, Divider, Chip, Button, Select, MenuItem, TextField } from '@mui/material'
import {
  Assignment as TasksIcon,
  TrendingUp as TrendIcon,
  ArrowForward as ArrowIcon,
  Leaderboard as LeadsIcon,
  Store as StoreIcon,
  BarChart as DealIcon,
  AttachMoney as RevenueIcon,
  EmojiEvents as WonIcon,
  CalendarToday as CalIcon,
} from '@mui/icons-material'
import { useNavigate } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { PageHeader } from '../components/shared/PageHeader'
import { StatCard } from '../components/shared/StatCard'
import { PipelineSummaryCard } from '../components/shared/PipelineSummaryCard'
import { StatusBadge } from '../components/ui/StatusBadge'
import { themeColors } from '../app/layout/theme'
import { useTasks } from '../lib/hooks/useTasks'
import { tasksApi } from '../lib/api/tasksApi'
import { customersApi } from '../lib/api/customersApi'
import { leadsApi } from '../lib/api/leadsApi'
import { dealsApi, Deal } from '../lib/api/dealsApi'
import { salesApi } from '../lib/api/salesApi'
import { getInitials } from '../util/helpers'
import { useOrgFilter, useOrgSelector } from '../context/OrgSelectorContext'
import { authStore } from '../lib/stores/authStore'
import { lobsApi } from '../lib/api/tenantsApi'
import { maskPhoneNumber } from '../utils/phoneUtils'
import {
  FilterList as FilterListIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
} from '@mui/icons-material'
import {
  UnifiedFilterDialog,
  UnifiedFilters,
  FilterDef,
  countActiveFilters,
  EMPTY_UNIFIED_FILTERS,
  applyUnifiedDateFilter,
} from '../components/shared/UnifiedFilterDialog'

function getAvatarStyle(name: string) {
  return { bgcolor: `${themeColors.primary}12`, color: themeColors.primary };

}

// ── Format helpers ─────────────────────────────────────────────────────────────

function fmtCurrency(n: number) {
  if (n === 0) return '₹0'
  if (n >= 1_00_00_000) return `₹${(n / 1_00_00_000).toFixed(n % 1_00_00_000 === 0 ? 0 : 2)}Cr`
  if (n >= 1_00_000)    return `₹${(n / 1_00_000).toFixed(n % 1_00_000 === 0 ? 0 : 2)}L`
  if (n >= 1_000)       return `₹${(n / 1_000).toFixed(0)}K`
  return `₹${n}`
}

function fmtDate(iso: string | null) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })
}

function getDateRange(preset: string): { from: Date | null; to: Date | null } {
  const now = new Date()
  if (preset === 'TODAY') {
    const s = new Date(now); s.setHours(0, 0, 0, 0)
    return { from: s, to: now }
  }
  if (preset === 'YESTERDAY') {
    const s = new Date(now); s.setDate(s.getDate() - 1); s.setHours(0, 0, 0, 0)
    const e = new Date(s); e.setHours(23, 59, 59, 999)
    return { from: s, to: e }
  }
  if (preset === 'LAST_7') {
    const s = new Date(now); s.setDate(s.getDate() - 7); s.setHours(0, 0, 0, 0)
    return { from: s, to: now }
  }
  if (preset === 'A_MONTH_AGO') {
    const s = new Date(now); s.setMonth(s.getMonth() - 1); s.setHours(0, 0, 0, 0)
    return { from: s, to: now }
  }
  if (preset === '3_MONTHS') {
    const s = new Date(now); s.setMonth(s.getMonth() - 3); s.setHours(0, 0, 0, 0)
    return { from: s, to: now }
  }
  if (preset === '6_MONTHS') {
    const s = new Date(now); s.setMonth(s.getMonth() - 6); s.setHours(0, 0, 0, 0)
    return { from: s, to: now }
  }
  if (preset === '1_YEAR') {
    const s = new Date(now); s.setFullYear(s.getFullYear() - 1); s.setHours(0, 0, 0, 0)
    return { from: s, to: now }
  }
  return { from: null, to: null }
}

export default function HomePage() {
  const navigate = useNavigate()
  // useTasks() is kept for task list/kanban views — NOT used on homepage stat card
  // Homepage uses getMySummary() instead (single COUNT query, no record limit)

  // Org filter from navbar (SuperAdmin: scoped to selected org; others: empty)
  const orgFilter = useOrgFilter()

  // ── LOB selector (Admin / SuperAdmin only) ────────────────────────────────
  // Use the same OrgId/LobId-based SuperAdmin detection as the navbar org selector
  // (usePermission's roleName-string check doesn't reliably match every SuperAdmin's
  // role label, which previously hid this dropdown for SuperAdmins entirely).
  const { isSuperAdmin } = useOrgSelector()
  const userOrgId  = authStore.user?.orgId ?? ''
  const userLobId  = authStore.user?.lobId ?? ''
  const isOrgAdmin = !isSuperAdmin && !!userOrgId && !userLobId

  // SuperAdmin: must first pick an org via the navbar selector before LOBs make sense.
  // Org Admin: LOBs are always scoped to their own (fixed) org.
  const lobOrgId = isSuperAdmin ? orgFilter.orgId : (isOrgAdmin ? userOrgId : undefined)

  const { data: dashboardLobs = [] } = useQuery({
    queryKey: ['admin-lobs', lobOrgId],
    queryFn: () => lobsApi.getAll(lobOrgId!),
    enabled: (isSuperAdmin || isOrgAdmin) && !!lobOrgId,
    staleTime: 30_000,
  })

  const [selectedLobId, setSelectedLobId] = useState<string>(() => {
    try { return sessionStorage.getItem('crm-dashboard-lob-id') ?? '' } catch { return '' }
  })

  // Clear a stale LOB selection whenever the in-scope org changes (or LOB picking
  // becomes unavailable, e.g. SuperAdmin switches back to "All Organizations").
  useEffect(() => {
    if (!lobOrgId) { setSelectedLobId('') ; return }
    if (selectedLobId && !dashboardLobs.some(l => l.id === selectedLobId)) setSelectedLobId('')
  }, [lobOrgId, dashboardLobs, selectedLobId])

  useEffect(() => {
    try { sessionStorage.setItem('crm-dashboard-lob-id', selectedLobId) } catch { /* ignore */ }
  }, [selectedLobId])

  const lobIdsFilter = selectedLobId ? [selectedLobId] : undefined

  const [filters, setFilters] = useState<UnifiedFilters>(() => {
    const saved = sessionStorage.getItem('crm-dashboard-filters')
    if (saved) {
      try { return JSON.parse(saved) } catch { }
    }
    return EMPTY_UNIFIED_FILTERS
  })

  useEffect(() => {
    sessionStorage.setItem('crm-dashboard-filters', JSON.stringify(filters))
  }, [filters])

  const handleDateChange = (e: any) => {
    const preset = e.target.value
    if (preset === 'ALL' || !preset) {
      setFilters({ ...EMPTY_UNIFIED_FILTERS, datePreset: '' })
      return
    }
    if (preset === 'CUSTOM') {
      setFilters(prev => ({ ...prev, datePreset: 'CUSTOM' }))
      return
    }
    const { from, to } = getDateRange(preset)
    setFilters({
      ...EMPTY_UNIFIED_FILTERS,
      datePreset: preset,
      dateFrom: from ? from.toISOString().slice(0, 10) : '',
      dateTo: to ? to.toISOString().slice(0, 10) : ''
    })
  }



  const dateFilters = {
    fromDate: filters.dateFrom ? `${filters.dateFrom}T00:00:00Z` : undefined,
    toDate: filters.dateTo ? `${filters.dateTo}T23:59:59Z` : undefined,
  }

  const { data: customersData } = useQuery({
    queryKey: ['customers', { page: 1, pageSize: 6, ...orgFilter, ...dateFilters, lobIdsFilter }],
    queryFn: () => customersApi.getAll({ page: 1, pageSize: 6, ...orgFilter, ...dateFilters, lobIds: lobIdsFilter }),
  })

  const { data: allCustomersData } = useQuery({
    queryKey: ['customers-count', orgFilter, dateFilters, lobIdsFilter],
    queryFn: () => customersApi.getAll({ page: 1, pageSize: 1, ...orgFilter, ...dateFilters, lobIds: lobIdsFilter }),
  })

  const { data: leadsSummaryData } = useQuery({
    queryKey: ['leads-summary', orgFilter, dateFilters, lobIdsFilter],
    queryFn: () => leadsApi.getSummary({
      orgIds: orgFilter.orgId ? [orgFilter.orgId] : undefined,
      lobIds: lobIdsFilter,
      ...dateFilters
    }),
  })

  const { data: openLeadsData } = useQuery({
    queryKey: ['leads-open', orgFilter, dateFilters, lobIdsFilter],
    queryFn: () => leadsApi.getAll({ page: 1, pageSize: 6, statuses: ['NEW'], ...orgFilter, ...dateFilters, lobIds: lobIdsFilter }),
  })

  // ── Deal queries ──────────────────────────────────────────────────────────────
  const { data: openDealsData } = useQuery({
    queryKey: ['deals-open', orgFilter, dateFilters, lobIdsFilter],
    queryFn: () => dealsApi.getAll({
      pageSize: 5,
      statuses: ['OPEN'],
      orgIds: orgFilter.orgId ? [orgFilter.orgId] : undefined,
      lobIds: lobIdsFilter,
      ...dateFilters
    }),
  })

  const { data: dealsSummary } = useQuery({
    queryKey: ['deals-summary-stats', orgFilter, dateFilters, lobIdsFilter],
    queryFn: () => dealsApi.getSummary({
      orgIds: orgFilter.orgId ? [orgFilter.orgId] : undefined,
      lobIds: lobIdsFilter,
      ...dateFilters
    }),
  })

  const { data: salesRevenueSummary } = useQuery({
    queryKey: ['sales-revenue-home', orgFilter, dateFilters, lobIdsFilter],
    queryFn: () => salesApi.getRevenue({
      orgIds: orgFilter.orgId ? [orgFilter.orgId] : undefined,
      lobIds: lobIdsFilter,
      fromDate: dateFilters?.fromDate,
      toDate: dateFilters?.toDate,
    }),
  })

  const { data: salesSummaryData } = useQuery({
    queryKey: ['sales-summary-stats', orgFilter, dateFilters, lobIdsFilter],
    queryFn: () => salesApi.getSummary({
      orgIds: orgFilter.orgId ? [orgFilter.orgId] : undefined,
      lobIds: lobIdsFilter,
      ...dateFilters
    }),
  })

  const recentCustomers = customersData?.items ?? []
  const totalCustomers  = allCustomersData?.total ?? 0
  const totalLeads      = leadsSummaryData?.totalLeads ?? 0
  const openLeads       = openLeadsData?.items ?? []
  
  const getLeadCountByStatus = (statusStr: string) => 
    leadsSummaryData?.statusSummaries?.find(s => s.status === statusStr)?.count ?? 0

  const leadNewCount = getLeadCountByStatus('NEW')
  const leadConvertedCount = getLeadCountByStatus('CONVERTED')
  const leadJunkedCount = getLeadCountByStatus('JUNK')
  const leadOpenCount = totalLeads - leadNewCount - leadConvertedCount - leadJunkedCount

  // Pending Tasks stat — lightweight summary from dedicated API
  const { data: tasksSummary } = useQuery({
    queryKey: ['tasks-summary', orgFilter, lobIdsFilter],
    queryFn: () => tasksApi.getMySummary({
      orgIds: orgFilter.orgId ? [orgFilter.orgId] : undefined,
      lobIds: lobIdsFilter,
    }),
    staleTime: 60_000,
  })

  // ── Deal stats ────────────────────────────────────────────────────────────────
  const openDealsList  = openDealsData?.items ?? []
  const openDealsCount = dealsSummary?.openDealsCount ?? openDealsData?.total ?? 0
  const openPipeline   = dealsSummary?.openPipelineAmount ?? openDealsList.reduce((s, d) => s + (d.amount ?? 0), 0)
  const totalRevenue   = salesRevenueSummary?.totalRevenue ?? 0

  const getSalesCountByStatus = (statusStr: string) => 
    salesSummaryData?.statusSummaries?.find(s => s.status.toLowerCase() === statusStr.toLowerCase())?.count ?? 0

  const salesNewCount = getSalesCountByStatus('New')
  const salesOpenCount = getSalesCountByStatus('Open') || getSalesCountByStatus('Onboarded')
  const salesClosedCount = getSalesCountByStatus('Closed') || getSalesCountByStatus('Won')
  const salesLostCount = getSalesCountByStatus('Lost')

  const fmtRevenue = (n: number) => {
    if (n === 0) return '₹0'
    if (n >= 1_00_00_000) return `₹${(n / 1_00_00_000).toFixed(n % 1_00_00_000 === 0 ? 0 : 2)}Cr`
    if (n >= 1_00_000)    return `₹${(n / 1_00_000).toFixed(n % 1_00_000 === 0 ? 0 : 2)}L`
    return `₹${n.toLocaleString('en-IN')}`
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <PageHeader
        title="Home"
        actions={
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
            {(isSuperAdmin || isOrgAdmin) && (
              <Select
                size="small"
                value={selectedLobId}
                onChange={(e) => setSelectedLobId(e.target.value)}
                displayEmpty
                disabled={!lobOrgId}
                sx={{
                  height: 36,
                  minWidth: 150,
                  bgcolor: `${themeColors.primary}02`,
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: themeColors.primary,
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: `${themeColors.primary}30` },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: themeColors.primary },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: themeColors.primary, borderWidth: '1px' },
                  '& .MuiSvgIcon-root': { color: themeColors.primary },
                  '& .MuiSelect-select': { py: 0, display: 'flex', alignItems: 'center' },
                }}
              >
                <MenuItem value="" sx={{ fontSize: 13 }}>
                  {!lobOrgId ? (isSuperAdmin ? 'Select an org first' : 'All LOBs') : 'All LOBs'}
                </MenuItem>
                {dashboardLobs.map(l => (
                  <MenuItem key={l.id} value={l.id} sx={{ fontSize: 13 }}>{l.name}</MenuItem>
                ))}
              </Select>
            )}
            {filters.datePreset === 'CUSTOM' && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={{ fontSize: 13, color: '#64748b' }}>From:</Typography>
                <TextField
                  type="date"
                  size="small"
                  value={filters.dateFrom || ''}
                  onChange={(e) => setFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
                  sx={{ 
                    width: 130, 
                    '& .MuiOutlinedInput-root': { 
                      bgcolor: '#fff', 
                      borderRadius: '8px', 
                      height: 36, 
                      fontSize: '13px',
                      fontWeight: 600,
                      color: themeColors.text.secondary,
                      boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                      '& fieldset': { borderColor: themeColors.border },
                      '&:hover fieldset': { borderColor: '#cbd5e1' },
                      '&.Mui-focused fieldset': { borderColor: themeColors.primary, borderWidth: '1px' },
                    } 
                  }}
                />
                <Typography variant="body2" sx={{ fontSize: 13, color: '#64748b', ml: 0.5 }}>To:</Typography>
                <TextField
                  type="date"
                  size="small"
                  value={filters.dateTo || ''}
                  onChange={(e) => setFilters(prev => ({ ...prev, dateTo: e.target.value }))}
                  sx={{ 
                    width: 130, 
                    '& .MuiOutlinedInput-root': { 
                      bgcolor: '#fff', 
                      borderRadius: '8px', 
                      height: 36, 
                      fontSize: '13px',
                      fontWeight: 600,
                      color: themeColors.text.secondary,
                      boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                      '& fieldset': { borderColor: themeColors.border },
                      '&:hover fieldset': { borderColor: '#cbd5e1' },
                      '&.Mui-focused fieldset': { borderColor: themeColors.primary, borderWidth: '1px' },
                    } 
                  }}
                />
              </Box>
            )}
            <Select
              size="small"
              value={filters.datePreset || 'ALL'}
              onChange={handleDateChange}
              displayEmpty
              sx={{
                height: 36,
                minWidth: '150px',
                bgcolor: `${themeColors.primary}02`,
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: 600,
                color: themeColors.primary,
                boxShadow: 'none',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: `${themeColors.primary}30`,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: themeColors.primary,
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: themeColors.primary,
                  borderWidth: '1px',
                },
                '& .MuiSvgIcon-root': { color: themeColors.primary },
                '& .MuiSelect-select': { 
                  py: 0,
                  display: 'flex',
                  alignItems: 'center',
                },
              }}
            >
              <MenuItem value="ALL" sx={{ fontSize: 13 }}>All Time</MenuItem>
              <MenuItem value="TODAY" sx={{ fontSize: 13 }}>Today</MenuItem>
              <MenuItem value="YESTERDAY" sx={{ fontSize: 13 }}>Yesterday</MenuItem>
              <MenuItem value="LAST_7" sx={{ fontSize: 13 }}>Last 7 Days</MenuItem>
              <MenuItem value="A_MONTH_AGO" sx={{ fontSize: 13 }}>A month ago</MenuItem>
              <MenuItem value="3_MONTHS" sx={{ fontSize: 13 }}>3 months</MenuItem>
              <MenuItem value="6_MONTHS" sx={{ fontSize: 13 }}>6 months</MenuItem>
              <MenuItem value="1_YEAR" sx={{ fontSize: 13 }}>1 year</MenuItem>
              <MenuItem value="CUSTOM" sx={{ fontSize: 13 }}>Custom Range</MenuItem>
            </Select>
          </Box>
        }
      />

      <Box sx={{ flex: 1, p: 3, overflow: 'auto' }}>
        {/* Stats Row */}
        <Grid container component="div" columnSpacing={2} rowGap={2.5} sx={{ mb: 3 }}>
          <Grid component="div" size={{ xs: 12, sm: 6, md: 3 }}>
            <StatCard
              title="Pending Tasks"
              value={tasksSummary?.pendingCount ?? 0}
              color={themeColors.error}
              // bgcolor={themeColors.errorLight}
              icon={<TasksIcon />}
            />
          </Grid>
          <Grid component="div" size={{ xs: 12, sm: 6, md: 3 }}>
            <StatCard
              title="Expected Value"
              value={fmtCurrency(salesSummaryData?.expectedValue ?? 0)}
              color={themeColors.warning}
              // bgcolor={themeColors.warningLight}
              icon={<TrendIcon />}
            />
          </Grid>
          <Grid component="div" size={{ xs: 12, sm: 6, md: 3 }}>
            <StatCard
              title="Total Revenue"
              value={fmtRevenue(totalRevenue)}
              color={themeColors.success}
              // bgcolor={themeColors.successLight}
              icon={<RevenueIcon />}
            />
          </Grid>
          <Grid component="div" size={{ xs: 12, sm: 6, md: 3 }}>
            <StatCard
              title="Total Customers"
              value={totalCustomers}
              color={themeColors.info}
              // bgcolor={themeColors.infoLight}
              icon={<WonIcon />}
            />
          </Grid>
        </Grid>

        {/* Pipeline Summary Row */}
        <Grid container component="div" spacing={2.5} sx={{ mb: 3 }}>
          <Grid component="div" size={{ xs: 12, md: 4 }}>
            <PipelineSummaryCard
              title="Leads"
              total={totalLeads}
              icon={<LeadsIcon />}
              color={themeColors.info}
              bgcolor={themeColors.surface}
              onClick={() => navigate('/leads')}
              subStatuses={[
                { label: 'New', value: leadNewCount },
                { label: 'Open', value: leadOpenCount },
                { label: 'Converted', value: leadConvertedCount },
                { label: 'Junked', value: leadJunkedCount },
              ]}
            />
          </Grid>
          <Grid component="div" size={{ xs: 12, md: 4 }}>
            <PipelineSummaryCard
              title="Deals"
              total={dealsSummary?.totalDeals}
              icon={<DealIcon />}
              color={themeColors.success}
              bgcolor={themeColors.surface}
              onClick={() => navigate('/deals')}
              subStatuses={[
                { label: 'New', value: dealsSummary?.newDealsCount ?? 0 },
                { label: 'Open', value: openDealsCount },
                { label: 'Closed', value: dealsSummary?.wonDealsCount ?? 0 },
                { label: 'Lost', value: dealsSummary?.lostDealsCount ?? 0 },
              ]}
            />
          </Grid>
          <Grid component="div" size={{ xs: 12, md: 4 }}>
            <PipelineSummaryCard
              title="Sales"
              total={salesSummaryData?.totalSales ?? 0}
              icon={<StoreIcon />}
              color={themeColors.warning}
              bgcolor={themeColors.surface}
              onClick={() => navigate('/sales')}
              subStatuses={[
                { label: 'New', value: salesNewCount },
                { label: 'Open', value: salesOpenCount },
                { label: 'Closed', value: salesClosedCount },
                { label: 'Lost', value: salesLostCount },
              ]}
            />
          </Grid>
        </Grid>

        {/* Main Grid — 3 equal columns */}
        <Grid container component="div" spacing={2.5}>

          {/* ── Col 1: New Leads ───────────────────────────────────────────── */}
          <Grid component="div" size={{ xs: 12, md: 4 }}>
            <Card
              sx={{
                borderRadius: '12px',
                border: `1px solid ${themeColors.border}`,
                boxShadow: themeColors.shadow.sm,
                height: 450,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box
                sx={{
                  px: 2.5, py: 1.5,
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  borderBottom: `1px solid ${themeColors.border}`,
                  background: themeColors.surface,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 28, height: 28, borderRadius: '6px',
                      background: `${themeColors.primary}12`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    <LeadsIcon sx={{ fontSize: 16, color: themeColors.primary }} />
                  </Box>
                  <Typography sx={{ fontWeight: 600, fontSize: '14px', color: themeColors.text.primary }}>New Leads</Typography>
                  <Chip
                    label={openLeadsData?.total ?? 0}
                    size="small"
                    sx={{
                      height: 18, fontSize: '10.5px', fontWeight: 600,
                      backgroundColor: `${themeColors.primary}15`, color: themeColors.primary,
                      borderRadius: '4px',
                      '& .MuiChip-label': { px: '6px' }
                    }}
                  />
                </Box>
                <Button
                  endIcon={<ArrowIcon sx={{ fontSize: 14 }} />}
                  size="small"
                  sx={{ fontSize: '12px', color: themeColors.primary, fontWeight: 500 }}
                  onClick={() => navigate('/leads')}
                >
                  View All
                </Button>
              </Box>
              <CardContent sx={{
                p: '0 !important',
                flex: 1,
                overflowY: 'auto',
                '&::-webkit-scrollbar': {
                  width: '6px',
                },
                '&::-webkit-scrollbar-track': {
                  background: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#e5e7eb',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  background: '#d1d5db',
                  borderRadius: '4px',
                },
              }}>
                {openLeads.length === 0 ? (
                  <Box sx={{ py: 6, textAlign: 'center' }}>
                    <LeadsIcon sx={{ fontSize: 48, color: themeColors.text.muted, mb: 1, opacity: 0.4 }} />
                    <Typography sx={{ color: themeColors.text.secondary, fontSize: '13px' }}>
                      No new leads at the moment
                    </Typography>
                  </Box>
                ) : (
                  openLeads.slice(0, 6).map((lead, idx) => (
                    <Box key={lead.id}>
                      <Box
                        sx={{
                          display: 'flex', alignItems: 'center', gap: 1.5,
                          px: 2.5, py: 1.75, cursor: 'pointer',
                          transition: 'all 0.15s ease',
                          '&:hover': { backgroundColor: themeColors.surfaceHover },
                        }}
                        onClick={() => navigate(`/leads/${lead.id}`)}
                      >
                        <Avatar
                          sx={{
                            width: 36, height: 36, fontSize: '13px', fontWeight: 600,
                            ...getAvatarStyle(lead.name),
                          }}
                        >
                          {getInitials(lead.name.split(' ')[0], lead.name.split(' ').slice(1).join(' '))}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 600, fontSize: '13px', color: themeColors.text.primary }} noWrap>
                            {lead.name}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '11.5px', color: themeColors.text.secondary,
                              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                            }}
                          >
                            {maskPhoneNumber(lead.phone)} · {lead.source}
                          </Typography>
                        </Box>
                        <StatusBadge status={lead.status} />
                      </Box>
                      {idx < openLeads.slice(0, 5).length - 1 && (
                        <Divider sx={{ mx: 2.5, borderColor: themeColors.borderLight }} />
                      )}
                    </Box>
                  ))
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* ── Col 2: Open Deals ─────────────────────────────────────────────── */}
          <Grid component="div" size={{ xs: 12, md: 4 }}>
            <Card
              sx={{
                borderRadius: '12px',
                border: `1px solid ${themeColors.border}`,
                boxShadow: themeColors.shadow.sm,
                height: 450,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {/* Header */}
              <Box
                sx={{
                  px: 2.5, py: 1.5,
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  borderBottom: `1px solid ${themeColors.border}`,
                  background: themeColors.surface,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 28, height: 28, borderRadius: '6px',
                      background: `${themeColors.primary}12`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    <DealIcon sx={{ fontSize: 16, color: themeColors.primary }} />
                  </Box>
                  <Typography sx={{ fontWeight: 600, fontSize: '14px', color: themeColors.text.primary }}>Open Deals</Typography>
                  <Chip
                    label={openDealsCount}
                    size="small"
                    sx={{
                      height: 18, fontSize: '10.5px', fontWeight: 600,
                      bgcolor: `${themeColors.primary}12`, color: themeColors.primary,
                      borderRadius: '4px',
                      '& .MuiChip-label': { px: '6px' }
                    }}
                  />
                </Box>
                <Button
                  endIcon={<ArrowIcon sx={{ fontSize: 14 }} />}
                  size="small"
                  sx={{ fontSize: '12px', color: themeColors.primary, fontWeight: 500 }}
                  onClick={() => navigate('/deals')}
                >
                  View All
                </Button>
              </Box>

              {/* Pipeline sub-header */}
              {/* {openPipeline > 0 && (
                <Box sx={{ px: 2.5, py: 0.75, bgcolor: '#faf5ff', borderBottom: `1px solid #ede9fe` }}>
                  <Typography sx={{ fontSize: '12px', color: '#7c3aed', fontWeight: 600 }}>
                    {fmtCurrency(openPipeline)} in pipeline
                  </Typography>
                </Box>
              )} */}

              {/* Body */}
              <CardContent sx={{
                p: '0 !important',
                flex: 1,
                overflowY: 'auto',
                '&::-webkit-scrollbar': {
                  width: '6px',
                },
                '&::-webkit-scrollbar-track': {
                  background: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#e5e7eb',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  background: '#d1d5db',
                  borderRadius: '4px',
                },
              }}>
                {openDealsList.length === 0 ? (
                  <Box sx={{ py: 6, textAlign: 'center' }}>
                    <DealIcon sx={{ fontSize: 40, color: '#d1d5db', mb: 1 }} />
                    <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>No open deals yet</Typography>
                    <Typography sx={{ fontSize: '12px', color: 'text.disabled', mt: 0.5 }}>
                      Create a deal to start tracking your pipeline
                    </Typography>
                  </Box>
                ) : (
                  openDealsList.slice(0, 5).map((deal: Deal, idx: number) => (
                    <Box key={deal.id}>
                      <Box
                        onClick={() => navigate(`/deals/${deal.id}`)}
                        sx={{
                          display: 'flex', alignItems: 'center', gap: 1.5,
                          px: 2.5, py: 1.75, cursor: 'pointer',
                          transition: 'all 0.15s ease',
                          '&:hover': { bgcolor: themeColors.surfaceHover },
                        }}
                      >
                        <Box
                          sx={{
                            width: 36, height: 36, borderRadius: '9px',
                            bgcolor: `${themeColors.primary}12`, flexShrink: 0,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}
                        >
                          <DealIcon sx={{ fontSize: 18, color: themeColors.primary }} />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: '13px', fontWeight: 600, color: 'text.primary' }} noWrap>
                            {deal.title}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
                            <Chip
                              label={deal.stage}
                              size="small"
                              sx={{
                                height: 16, fontSize: '9.5px', fontWeight: 600,
                                bgcolor: `${themeColors.primary}12`, color: themeColors.primary,
                                borderRadius: '4px', '& .MuiChip-label': { px: '5px' },
                              }}
                            />
                            {deal.expectedCloseDate && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                                <CalIcon sx={{ fontSize: 11, color: '#9ca3af' }} />
                                <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
                                  {fmtDate(deal.expectedCloseDate)}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>
                        <Typography sx={{ fontSize: '13px', fontWeight: 700, color: themeColors.primary, flexShrink: 0 }}>
                          {deal.amount != null ? fmtCurrency(deal.amount) : '—'}
                        </Typography>
                      </Box>
                      {idx < openDealsList.slice(0, 5).length - 1 && (
                        <Divider sx={{ mx: 2.5, borderColor: themeColors.borderLight }} />
                      )}
                    </Box>
                  ))
                )}
              </CardContent>

              {/* Won revenue footer */}
              {openDealsList.length > 0 && (dealsSummary?.wonDealsCount ?? 0) > 0 && (
                <Box
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5,
                    px: 2.5, py: 1.25,
                    borderTop: `1px solid ${themeColors.border}`,
                    bgcolor: '#f0fdf4',
                  }}
                >
                  <WonIcon sx={{ fontSize: 18, color: '#16a34a' }} />
                  <Typography sx={{ fontSize: '12.5px', color: '#166534', fontWeight: 600, flex: 1 }}>
                    {(dealsSummary?.wonDealsCount ?? 0)} deal{(dealsSummary?.wonDealsCount ?? 0) !== 1 ? 's' : ''} won
                  </Typography>
                  <Typography sx={{ fontSize: '13px', fontWeight: 800, color: '#16a34a' }}>
                    {fmtCurrency(totalRevenue)}
                  </Typography>
                </Box>
              )}
            </Card>
          </Grid>

          {/* ── Col 3: My Customers ──────────────────────────────────────────────── */}
          <Grid component="div" size={{ xs: 12, md: 4 }}>
            <Card
              sx={{
                borderRadius: '12px',
                border: `1px solid ${themeColors.border}`,
                boxShadow: themeColors.shadow.sm,
                height: 450,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box
                sx={{
                  px: 2.5,
                  py: 1.5,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  borderBottom: `1px solid ${themeColors.border}`,
                  background: themeColors.surface,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 28, height: 28, borderRadius: '6px',
                      background: `${themeColors.primary}12`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    <StoreIcon sx={{ fontSize: 16, color: themeColors.primary }} />
                  </Box>
                  <Typography sx={{ fontWeight: 600, fontSize: '14px', color: themeColors.text.primary }}>My Customers</Typography>
                  <Chip
                    label={totalCustomers}
                    size="small"
                    sx={{
                      height: 18, fontSize: '10.5px', fontWeight: 600,
                      backgroundColor: `${themeColors.primary}12`, color: themeColors.primary,
                      borderRadius: '4px',
                      '& .MuiChip-label': { px: '6px' }
                    }}
                  />
                </Box>
                <Button
                  endIcon={<ArrowIcon sx={{ fontSize: 14 }} />}
                  size="small"
                  sx={{ fontSize: '12px', color: themeColors.primary, fontWeight: 500 }}
                  onClick={() => navigate('/customers')}
                >
                  View All
                </Button>
              </Box>
              <CardContent sx={{
                p: '0 !important',
                flex: 1,
                overflowY: 'auto',
                '&::-webkit-scrollbar': {
                  width: '6px',
                },
                '&::-webkit-scrollbar-track': {
                  background: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#e5e7eb',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  background: '#d1d5db',
                  borderRadius: '4px',
                },
              }}>
                {recentCustomers.length === 0 ? (
                  <Box sx={{ py: 6, textAlign: 'center' }}>
                    <StoreIcon sx={{ fontSize: 48, color: themeColors.text.muted, mb: 1, opacity: 0.4 }} />
                    <Typography sx={{ color: themeColors.text.secondary, fontSize: '13px' }}>
                      No customers yet — add your first customer
                    </Typography>
                  </Box>
                ) : (
                  recentCustomers.map((customer, idx) => (
                    <Box key={customer.id}>
                      <Box
                        sx={{
                          display: 'flex', alignItems: 'center', gap: 1.5,
                          px: 2.5, py: 1.75, cursor: 'pointer',
                          transition: 'all 0.15s ease',
                          '&:hover': { backgroundColor: themeColors.surfaceHover },
                        }}
                        onClick={() => navigate(`/customers/${customer.id}`)}
                      >
                        <Avatar
                          sx={{
                            width: 36, height: 36, fontSize: '13px', fontWeight: 600,
                            ...getAvatarStyle(customer.name),
                          }}
                        >
                          {getInitials(customer.name.split(' ')[0], customer.name.split(' ').slice(1).join(' '))}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontWeight: 600, fontSize: '13px', color: themeColors.text.primary,
                              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                            }}
                          >
                            {customer.name}
                          </Typography>
                          <Typography sx={{ fontSize: '11.5px', color: themeColors.text.secondary }}>
                            {customer.companyName || 'Customer'} · {customer.city ?? customer.source}
                          </Typography>
                        </Box>
                        <Typography sx={{ fontSize: '11.5px', color: themeColors.text.secondary }}>
                          {maskPhoneNumber(customer.phone)}
                        </Typography>
                      </Box>
                      {idx < recentCustomers.length - 1 && (
                        <Divider sx={{ mx: 2.5, borderColor: themeColors.borderLight }} />
                      )}
                    </Box>
                  ))
                )}
              </CardContent>
            </Card>
          </Grid>

        </Grid>
      </Box>

      </Box>
    )
}
